import { FinePrints } from './fine-print.domain';
import { WeightedReturnType } from './weighted-return-type.domain';
import {
  Account,
  AccountDivisionType,
  AccountGroup,
  AccountHelper,
  AccountNumber,
  AccountType,
  CashBalance,
  GroupNumber
} from './account.domain';
import { Currency } from './market.domain';

export enum AGGREGATION_TYPES {
  SORTED_GROUP_NUMBER_LIST = 'SORTED_GROUP_NUMBER_LIST',
  ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE = 'ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE',
  ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE_CURRENCY = 'ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE_CURRENCY',
  ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER = 'ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER',
  ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER_CURRENCY = 'ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER_CURRENCY',
  PORTFOLIO_CASH_SECURITY_TOTAL = 'PORTFOLIO_CASH_SECURITY_TOTAL',
  ACCOUNT_BALANCES_FINEPRINT = 'ACCOUNT_BALANCES_FINEPRINT'
}

export enum REAL_TIME_AGGREGATION_TYPES {
  REAL_TIME_CASH_DATA_TOTAL = 'REAL_TIME_CASH_DATA_TOTAL',
  REAL_TIME_CASH_TOTAL_GROUPED_BY_TYPE = 'REAL_TIME_CASH_TOTAL_GROUPED_BY_TYPE',
  REAL_TIME_CASH_TOTAL_GROUPED_BY_CURRENCY = 'REAL_TIME_CASH_TOTAL_GROUPED_BY_CURRENCY',
  REAL_TIME_CASH_DATA_GROUPED_BY_TYPE_CURRENCY = 'REAL_TIME_CASH_DATA_GROUPED_BY_TYPE_CURRENCY'
}

export interface Mapper<F, T> {
  map(item: F): T;
}

export type Partial<T> = { [P in keyof T]?: T[P] };
export type AccountPartial = Partial<Account>;
export type AccountWithBalances = Account & BalanceTotals;
export type AccountWithCashTotals = Account & CashTotals & BalanceDateAware;
export type BalancesAccountGroup = AccountGroup & BalanceDateAware;

/**
 * User can choose to view their balances in either 'currency' or 'consolidated' tile/list views.
 */
export class BalancesView {
  constructor(
    public key: string,
    public view: string,
    public consolidatedView: boolean,
    public helpArticleId: string
  ) {}
}

export interface AccountTypeAware {
  accountType: AccountType;
}

export interface AggregationTypeAware {
  aggregationResponseType: AGGREGATION_TYPES;
}

export interface BalanceDateAware {
  balanceDate: Date;
  masterAccountId: string;
}

export type GroupLevelFineprints = Record<string, Array<string>>;

/**
 * @see FinePrints at td-wb-webbroker\src\webbroker-core\components\fine-print\fine-print.service.ts
 */
export interface BalanceFineprints {
  fineprints: FinePrints;
  groupLevelFineprints: GroupLevelFineprints;
}

export class RateOfReturn {
  asOfDate: Date;
  periodType: string;
  weightedReturnType: WeightedReturnType;
  consolidatedPerformances: Map<Currency, number>;
}

export enum BalancesRowType {
  REGULAR,
  SUBTOTAL_CAD,
  SUBTOTAL_USD,
  TOTAL,
  COMMENT
}

export class BalanceTotalsRow {
  id?: string;

  constructor(public rowType: BalancesRowType) {}
}

/**
 * BalancesRow adapts account with totals to a form suitable for ngx-datatable templates.
 */
export type BalancesRow = (BalanceTotals | AccountsAndTotals | AccountWithCashTotals | AccountWithBalances) & BalanceTotalsRow;

/**
 * There are a few domain interfaces that capture totals for Balances.
 * BalanceTotals is the lowest level structure that captures various aggregation results.
 *
 * @see AccountTotal in gain-loss.ts
 * @see AccountTotals in gain-loss.ts
 */
export class BalanceTotals {
  constructor(
    public totalCashBalance: Map<Currency, CashBalance> = undefined,
    public totalSecurityBalance: Map<Currency, CashBalance> = undefined,
    public totalMarginBalance: Map<Currency, CashBalance> = undefined,
    public totalBookValue: Map<Currency, CashBalance> = undefined,
    public totalBalance: Map<Currency, CashBalance> = undefined
  ) {}

  getAmount(propertyName: string, currency: Currency): number {
    const currencyMap: Map<Currency, CashBalance> = this[propertyName];
    if (currencyMap === undefined) {
      // if backend did not send a value (e.g. margin balance for non margin accounts), default it to 'N/A'
      return undefined;
    }
    return BalancesUtils.getAmount(currencyMap, currency);
  }
}

export class TotalsByCurrency {
  constructor(public result: Map<Currency, BalanceTotals> = new Map()) {
  }

  getAmount(propertyName: string, currency: Currency): number | string {
    const totals: BalanceTotals = (this.result instanceof Map) ? this.result.get(currency) : this.result[currency];
    return totals !== undefined ? totals.getAmount(propertyName, currency) : undefined;
  }
}

/**
 * Used by Balances page: a collection of accounts and calculated balance totals for them.
 * See AccountWithBalances for flat account fields/totals structure.
 */
export class AccountsAndTotals {
  owners?: string;

  constructor(
    public accounts: Array<Account>,
    public totals: BalanceTotals) {
  }

  isMargin(): boolean {
    return this.hasFirstAccount() && AccountHelper.isMarginAccount(this.accounts[0]);
  }

  getFirstAccountId(): string {
    return this.hasFirstAccount() ? this.accounts[0].id : undefined;
  }

  getFirstAccount(): Account {
    return this.hasFirstAccount() ? this.accounts[0] : undefined;
  }

  getAmount(propertyName: string, currency: Currency): number {
    return this.totals === undefined ? undefined : this.totals.getAmount(propertyName, currency);
  }

  private hasFirstAccount(): boolean {
    return this.accounts !== undefined && this.accounts[0] !== undefined;
  }
}

/**
 * Filter: SORTED_GROUP_NUMBER_LIST
 */
export interface AccountGroups {
  result: Array<BalancesAccountGroup>;
}

/**
 * Filter: ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE
 */
export interface AccountsAndTotalsGroupedByNumberType extends AggregationTypeAware {
  result: Map<GroupNumber, Map<AccountType, AccountsAndTotals>>;
}

/**
 * Filter: ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE_CURRENCY
 * ACCOUNTS_WITH_BALANCES GROUPED BY ACCOUNT_GROUP_NUMBER, ACCOUNT_TYPE, CURRENCY
 */
export interface AccountDataGroupedByNumberCurrency extends AggregationTypeAware {
  result: Map<GroupNumber, Map<AccountType, Map<Currency, AccountWithBalances>>>;
}

/**
 * Filter: ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER
 * TOTALS GROUPED BY ACCOUNT_GROUP_NUMBER
 */
export interface TotalsByGroup extends AggregationTypeAware {
  result: Map<GroupNumber, BalanceTotals>;
}

/**
 * Filter: ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER_CURRENCY
 * TOTALS GROUPED BY ACCOUNT_GROUP_NUMBER, CURRENCY
 */
export interface TotalsByGroupCurrency extends AggregationTypeAware {
  result: Map<GroupNumber, TotalsByCurrency>;
}

/**
 * PortfolioTotal - total balance of entire user's portfolio.
 * Filter: PORTFOLIO_CASH_SECURITY_TOTAL
 * FIXME: PORTFOLIO TOTAL BALANCE (ALL ACCOUNTS)
 */
export interface PortfolioTotal extends AggregationTypeAware {
  result: { totalBalance: Map<Currency, CashBalance> };
}

export class BalanceFineprintsResponse {
  result: BalanceFineprints;
}

/**
 * BalancesResponse represents balances backend response data structure.
 * These keys must match AGGREGATION_TYPES enum values.
 */
export interface BalancesResponse {
  SORTED_GROUP_NUMBER_LIST: AccountGroups;
  ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE: AccountsAndTotalsGroupedByNumberType;
  ACCOUNT_DATA_GROUPED_BY_NUMBER_TYPE_CURRENCY: AccountDataGroupedByNumberCurrency;
  ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER: TotalsByGroup;
  ACCOUNT_DATA_TOTAL_GROUPED_BY_NUMBER_CURRENCY: TotalsByGroupCurrency; // aka sub-totals
  PORTFOLIO_CASH_SECURITY_TOTAL: PortfolioTotal;
  ACCOUNT_BALANCES_FINEPRINT: BalanceFineprintsResponse;
}

/**
 * Balances domain describes data structure received from the backend.
 * For currency & consolidated list views it's used as is (augmented by accessors), while tile view requires some data transformation.
 */
export class Balances {

  exchangeRate: number;

  constructor(
    public accountGroups: Array<BalancesAccountGroup>,
    public accountsAndTotalsByGroupType: Map<GroupNumber, Map<AccountType, AccountsAndTotals>>,
    public accountsWithBalancesByGroupTypeCurrency: Map<GroupNumber, Map<AccountType, Map<Currency, AccountWithBalances>>>,

    public groupTotals: Map<GroupNumber, BalanceTotals>,
    public groupSubTotals: Map<GroupNumber, TotalsByCurrency>,
    public accountsTotals: Map<Currency, CashBalance>, // naming: portfolioTotals
    public balanceTimestamp: Date,

    public fineprints: BalanceFineprints,
    public tradeCountTotals?: TradeCountTotals
  ) {}

  getExchangeRate(): number {
    return this.exchangeRate;
  }

  getTotalBalance(currency: Currency): number {
    return (this.accountsTotals === undefined) ? undefined : BalancesUtils.getAmount(this.accountsTotals, currency);
  }

  isBalancesTradeCount(groupNumber: string): boolean {
    return this.tradeCountTotals !== undefined && this.tradeCountTotals[groupNumber] !== undefined;
  }

  getTradeCountTotal(groupNumber: string): number {
    return this.isBalancesTradeCount(groupNumber) ? this.tradeCountTotals[groupNumber].totalGroupTradeCount : undefined;
  }
}

// === Real-Time Cash Balances === //

/**
 * Real-Time cash balance and associate account.
 * Used for Real-Time balances REAL_TIME_CASH_TOTAL_GROUPED_BY_TYPE aggregation.
 */
export interface AccountAndTotalsResponse {
  account: Account;
  totals: CashTotalsResponse;
}

export interface CashTotalsResponse {
  realTimeValueDateBalance: Map<Currency, CashBalance>;
  netTradePendingSettlement: Map<Currency, CashBalance>;
  realTimeCash: Map<Currency, CashBalance>;
}

/**
 * Real-Time Cash totals class encapsulates aggregation results specific to real-time cash balances.
 */
export class CashTotals {
  constructor(
    public settledCash: Map<Currency, CashBalance> = new Map(),
    public cashOnHold: Map<Currency, CashBalance> = new Map(),
    public realTimeCash: Map<Currency, CashBalance> = new Map()
  ) {}

  getAmount(propertyName: string, currency: Currency): number {
    return BalancesUtils.getAmount(this[propertyName], currency);
  }
}

/**
 * RealTimeCashBalancesResponse represents backend response data structure.
 */
export interface RealTimeCashBalancesResponse {
  REAL_TIME_CASH_DATA_TOTAL: { result: CashTotalsResponse };
  REAL_TIME_CASH_TOTAL_GROUPED_BY_CURRENCY: { result: Map<Currency, CashTotalsResponse> };
  REAL_TIME_CASH_TOTAL_GROUPED_BY_TYPE: { result: Map<AccountType, AccountAndTotalsResponse> };
  REAL_TIME_CASH_DATA_GROUPED_BY_TYPE_CURRENCY: { result: Map<AccountNumber, AccountWithCashTotals> };
}

export class RealtimeBalances {
  constructor(
    public cashTotalsByGroup: CashTotals,
    public cashTotalsByGroupCurrency: Map<Currency, CashTotals>,
    public accountWithCashTotalsByGroupType: Map<AccountType, AccountWithCashTotals>,
    public accountWithCashTotalsByAccountNumber:  Map<AccountNumber, AccountWithCashTotals>,
    public balanceDate?: Date
  ) {}
}

// === Trade Count === //

export interface TradeCountByAccountType {
  rapCode: string;
  id: string;
  clientId: string;
  accountType: AccountType;
  currency: Currency;
  divisionType: AccountDivisionType; // TODO portfolio_v2.tradeCountDetails still returns AccountDivisionType, should be AccountPlatform
  tradeCountDate: Date;
  thisMonthDate: Date;
  lastMonthDate: Date;
  twoMonthAgoDate: Date;
  threeMonthAgoDate: Date;
  monthToDateCnt: number;
  lastMonthCnt: number;
  twoMonthAgoCnt: number;
  threeMonthAgoCnt: number;
}

export interface TradeCountMonthlyTotal {
  monthToDateCount: number;
  lastMonthCount: number;
  twoMonthAgoCount: number;
  threeMonthAgoCount: number;
  previousThreeMonthsCount: number;
}

export interface TradeCountByAccountTypeResponse {
  result: Array<TradeCountByAccountType>;
}

export interface TradeCountMonthlyTotalResponse {
  result: TradeCountMonthlyTotal;
}

export interface TradeCount {
  TRADE_COUNT_DATA_GROUP_BY_ACCOUNT_TYPE: TradeCountByAccountTypeResponse;
  TRADE_COUNT_MONTHLY_TOTAL_PER_CLIENT: TradeCountMonthlyTotalResponse;
}

// === Trade Count Totals === //

export interface TradeCountTotalsResponse {
  TRADE_COUNT_TOTAL_GROUP_BY_NUMBER: {
    result: TradeCountTotals;
  };
}

export type TradeCountTotals = Record<string, { totalGroupTradeCount: number }>;

export class BalancesUtils {

  /**
   * Return a number representing a dollar amount for a given currency.
   * Works with either a Map or JSON object as the input balanceAmount parameter.
   *
   * @param balanceAmount either a Map instance or JSON-like structure with keys being currency code (CAD|USD),
   *        and values being CashBalance like structure
   * @param currency requested currency for which to find the corresponding dollar amount
   */
  static getAmount(balanceAmount: Map<Currency, CashBalance> | Record<string, CashBalance>, currency: Currency): number {
    if (balanceAmount === undefined) {
      throw new Error('Cannot calculate the amount: balanceAmount is undefined.');
    }
    if (currency === undefined) {
      throw new Error('Cannot calculate the amount: currency is undefined.');
    }

    if (balanceAmount instanceof Map && balanceAmount.size > 0) {
      return balanceAmount.get(currency).amount;
    } else if (balanceAmount[currency] !== undefined) {
      return balanceAmount[currency].amount;
    }
    throw new Error('Cannot calculate the amount: unsupported balanceAmount. ' + JSON.stringify(balanceAmount));
  }

  static isEmptyString(value: string): boolean {
    return value === undefined || (typeof value === 'string' && value.length === 0);
  }
}

export interface CurrencyBalance {
  balanceDate?: Date;
  currency?: Currency;
  favorite?: boolean;

  number?: string;
  lastSettlementDate?: Date;
}

export type CurrencyBalancesRow = BalancesRow & CurrencyBalance;

export type TradeCountBalancesRow = BalancesRow & TradeCountByAccountType & TradeCountMonthlyTotal;

export type SummaryBalancesRow = BalancesRow & AccountsAndTotals;
